import { createContext, useState } from "react";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import {
  createAdminService,
  deleteAdminService,
  editAdminService,
  getAdminsService,
  getUsersService,
  revokeTokenAdminService,
} from "./../../../services/userServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "./../../../redux/actions/loadingActions/loadingActions";
import {
  baseRoute,
  isValidPhoneNumber,
  setCookies,
} from "./../../../utils/index";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";

export const adminAdminContext = createContext({
  admins: [],
  setAdmins: () => {},
  users: [],
  setUsers: () => {},
  admin: {},
  setAdmin: () => {},
  userSearch: "",
  setUserSearch: () => {},
  id: "",
  setId: () => {},
  first_name: "",
  setFirst_name: () => {},
  last_name: "",
  setLast_name: () => {},
  mobile: "",
  setMobile: () => {},
  password: "",
  setPassword: () => {},
  search_permission: null,
  setSearch_permission: () => {},
  permission_level: null,
  setPermission_level: () => {},
  totalCount: null,
  setTotalCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  limit: null,
  setLimit: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  isUserLoading: null,
  setIsUserLoading: () => {},
  handleGetUsers: () => {},
  handleCreateAdmin: () => {},
  handleEditAdmin: () => {},
  handleShowModal: () => {},
  handleCloseModal: () => {},
  handleGetAdmins: () => {},
  handleDeleteAdmin: () => {},
  handleRevokeTokenAdmin: () => {},
  handleLoginAdmin: () => {},
});

const AdminAdminContext = ({ children }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const [admins, setAdmins] = useState([]);
  const [users, setUsers] = useState([]);
  const [admin, setAdmin] = useState({});
  const [userSearch, setUserSearch] = useState("");
  const [id, setId] = useState("");
  const [first_name, setFirst_name] = useState("");
  const [last_name, setLast_name] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [search_permission, setSearch_permission] = useState("");
  const [permission_level, setPermission_level] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [limit, setLimit] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);
  const [isUserLoading, setIsUserLoading] = useState(false);

  const handleResetState = () => {
    setId("");
    setFirst_name("");
    setLast_name("");
    setMobile("");
    setPassword("");
    setPermission_level(0);
    setAdmin({});
  };

  // Create Admin Modal Operations
  const handleShowModal = (value) => {
    setIsShowModal(true);
    if (value) {
      setId(value?.id);
      setFirst_name(value?.first_name);
      setLast_name(value?.last_name);
      setMobile(value?.mobile);
      setPermission_level(value?.permission_level);
      setAdmin(value);
    }
  };
  const handleCloseModal = () => {
    setIsShowModal(false);
    handleResetState();
  };

  // Handle Get Users Method
  const handleGetUsers = async (event) => {
    event?.preventDefault();
    const objData = {
      search: userSearch,
      offset: 0,
      limit: 100,
    };

    try {
      setIsUserLoading(true);
      const { status, data } = await getUsersService(objData);
      if (status === 200) {
        setUsers(data.results);
        setTotalCount(data.count);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      setIsUserLoading(false);
    }
  };

  // Handle Get Admins Method
  const handleGetAdmins = async (event) => {
    event?.preventDefault();
    const isSearch = !isEmpty(event);

    const objData = {
      offset: ((isSearch ? 1 : pageNumber) - 1) * limit,
      limit,
      permission_level: search_permission,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getAdminsService(objData);
      if (status === 200) {
        setAdmins(data.results);
        setTotalCount(data.count);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create Admin Method
  const handleCreateAdmin = async (event) => {
    event?.preventDefault();

    if (!isValidPhoneNumber(mobile))
      return toastErrorMessage("شماره موبایل معتبر نمیباشد");

    const objData = {
      first_name,
      last_name,
      mobile,
      password,
      permission_level,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createAdminService(objData);
      if (status === 200 || status === 201) {
        toastSuccessMessage(
          `مدیر ${objData.first_name} ${objData.last_name} با موفقیت ایجاد شد`
        );
        handleCloseModal();
        handleGetAdmins();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage(response?.data?.error);
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Admin Method
  const handleEditAdmin = async (event) => {
    event?.preventDefault();

    if (!isValidPhoneNumber(mobile))
      return toastErrorMessage("شماره موبایل معتبر نمیباشد");

    const objData = {
      pk: id,
      first_name,
      last_name,
      mobile,
      username: mobile,
      password,
      permission_level,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editAdminService(objData);
      if (status === 200 || status === 201) {
        toastSuccessMessage(
          `مدیر ${objData.first_name}  ${objData.last_name} با موفقیت ویرایش شد`
        );
        handleCloseModal();
        handleGetAdmins();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage(response?.data?.error);
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete Admin Method
  const handleDeleteAdmin = async (pk) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteAdminService(pk);
      if (status === 200 || status === 204) {
        toastSuccessMessage(`مدیر ${pk} با موفقیت حذف شد`);
        handleGetAdmins();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Revoke Token Admin Method
  const handleRevokeTokenAdmin = async (pk) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await revokeTokenAdminService(pk);
      if (status === 200) {
        toastSuccessMessage(`نشست مدیر ${pk} با موفقیت بازنشانی شد`);
        handleGetAdmins();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  const handleLoginAdmin = (item) => {
    setCookies("accessToken", item.token);
    localStorage.setItem("role", item.permission_level);
    window.location.href = baseRoute(false, `/Admin/Dashboard`);
  };

  return (
    <adminAdminContext.Provider
      value={{
        admins,
        setAdmins,
        users,
        setUsers,
        admin,
        setAdmin,
        userSearch,
        setUserSearch,
        id,
        setId,
        first_name,
        setFirst_name,
        last_name,
        setLast_name,
        mobile,
        setMobile,
        password,
        setPassword,
        search_permission,
        setSearch_permission,
        permission_level,
        setPermission_level,
        totalCount,
        setTotalCount,
        pageNumber,
        setPageNumber,
        limit,
        setLimit,
        isShowModal,
        setIsShowModal,
        isUserLoading,
        setIsUserLoading,
        handleGetUsers,
        handleCreateAdmin,
        handleEditAdmin,
        handleShowModal,
        handleCloseModal,
        handleGetAdmins,
        handleDeleteAdmin,
        handleRevokeTokenAdmin,
        handleLoginAdmin,
      }}
    >
      {children}
    </adminAdminContext.Provider>
  );
};

export default AdminAdminContext;
